import React from "react";

import Stack from "@mui/material/Stack";
import Typography from "ui/Typography";
import IconButtonMinus from "components/buttons/IconButtonMinus";
import IconButtonFileInput from "components/IconButtonFileInput";
import SectionDescription from "components/SectionDescription";
import VectorLogo from "components/AppThemePreview/VectorSvg";
import classNames from "classnames";

import useLogoSection from "hooks/dialogs/appCustomizations/leftSide/useLogoSection";

import LogoIcon from "assets/svg/logo.svg";

const logoStyle = {
  height: "36px",
  width: "36px",
};

const LogoSection = () => {
  const { availableExtensions, onAddFile, onRemoveFile, logo, error } =
    useLogoSection();

  const isFile = logo instanceof File;
  const isSvgVector =
    typeof logo === "string" &&
    (logo.trim().startsWith("M") || logo.trim().startsWith("m"));

  const fileName = React.useMemo(() => {
    if (isFile) {
      return logo.name.match(/^[a-f0-9]{32}\.png$/i) ? "logo.png" : logo.name;
    }
    if (isSvgVector) return "logo.svg";
    return "logo.png";
  }, [logo, isFile, isSvgVector]);

  const renderLogoSection = () => {
    if (!logo) return null;

    const containerClass = classNames("logo-container", {
      "logo-error-container": error,
    });

    const renderLogo = () => {
      if (!logo) return null;

      if (isFile) {
        return (
          <img
            src={URL.createObjectURL(logo)}
            alt="App logo preview"
            style={logoStyle}
          />
        );
      }

      if (isSvgVector) {
        const path = logo.trim();
        return <VectorLogo path={path} style={logoStyle} />;
      }

      return null;
    };

    return (
      <div className={containerClass}>
        {renderLogo()}
        <div className="logo-description">
          <Typography variant="subtitle1">{fileName}</Typography>
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
        </div>
      </div>
    );
  };

  const renderLogoButton = () => {
    if (!logo) {
      return (
        <IconButtonFileInput
          extensions={availableExtensions}
          onChange={(e) => onAddFile(e.target.files)}
        />
      );
    }
    return <IconButtonMinus onClick={onRemoveFile} variant="header" />;
  };

  return (
    <Stack gap="16px">
      <SectionDescription
        title="Logo (optional)"
        description="Only .svg or .png files can be uploaded. The .svg file applies to the dark and light themes, while .png files can be uploaded separately for each theme."
        LeadIcon={LogoIcon}
        TrailComponent={renderLogoButton}
      />
      {renderLogoSection()}
    </Stack>
  );
};

export default LogoSection;
